import './SelectFiltersPopover.css';

import { IonIcon, IonPopover } from '@ionic/react';
import Button from '../../Button/Button';
import { searchOutline } from 'ionicons/icons';
import ArrowIcon from '../../ArrowIcon/ArrowIcon';
import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import Checkbox from '../../Checkbox/Checkbox';
import Input from '../../Input/Input';
import InputLabel from '../InputLabel/InputLabel';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { highlightMatch } from '../../../utils/highlightMatch';
import { useSuppressEscapeDefault } from '../../../hooks/shortcuts/useSuppressEscapeDefault';
import { isMandatoryFilter } from '../../../utils/isMandatoryFilter';
import { Dataset } from '../../../types/types';

interface SelectFiltersPopoverProps {
  popoverFilters: any[];
  setPopoverFilters: Dispatch<SetStateAction<any[]>>;
  submitPopoverFilters: (
    filters: any,
    getValueFromActiveFilters?: boolean
  ) => void;
  isSlaveFilter: boolean;
  dataset: Dataset;
}

const SelectFiltersPopover = ({
  popoverFilters,
  setPopoverFilters,
  submitPopoverFilters,
  isSlaveFilter,
  dataset,
}: SelectFiltersPopoverProps) => {
  const popoverRef = useRef<HTMLIonPopoverElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');
  const { t } = useTranslate();

  // While open, swallow Esc's browser default (e.g. fullscreen exit); Ionic still closes the popover via its own listener.
  useSuppressEscapeDefault(popoverOpen);

  const openPopover = (e: any) => {
    setSearchString('');
    popoverRef.current!.event = e;
    setPopoverOpen(true);
  };

  const setPopoverFilterValue = (value, datasetId, itemName) => {
    setPopoverFilters((v) =>
      v.map((dataset) => {
        if (dataset.id === datasetId) {
          const newItems = dataset.items.map((item) => {
            if (item.name === itemName) {
              return { ...item, value };
            } else {
              return { ...item };
            }
          });
          return { ...dataset, items: newItems };
        } else {
          return { ...dataset };
        }
      })
    );
  };

  // Single submit path: every dismiss (Apply, outside click, Esc) applies the filters.
  const selectFiltersSubmit = () => {
    submitPopoverFilters(popoverFilters);
    setPopoverOpen(false);
  };

  const displayedFilters = useMemo(() => {
    if (searchString.length) {
      const lowercaseSearch = searchString.toLowerCase();
      return popoverFilters
        .map((category) => {
          const newItems = category.items.filter(({ label }) =>
            label.toLowerCase().includes(lowercaseSearch)
          );
          return { ...category, items: newItems };
        })
        .filter(({ items }) => items.length > 0);
    }
    return popoverFilters;
  }, [popoverFilters, searchString]);

  const onPopoverDidPresent = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div>
      <Button
        onClick={openPopover}
        fill="clear"
        color="secondary"
        className="select-filters-btn"
      >
        <IonIcon icon={searchOutline} slot="start" />
        {t('filterUsing')}
        <ArrowIcon direction="down" color="dark" />
      </Button>
      <IonPopover
        ref={popoverRef}
        isOpen={popoverOpen}
        onDidDismiss={selectFiltersSubmit}
        onDidPresent={onPopoverDidPresent}
        showBackdrop={false}
        className="select-filters-popover"
      >
        <div className="select-filters-container">
          <div className="select-filters-list">
            {displayedFilters.map(({ id, caption, items }) => (
              <div className="select-filters-block" key={id}>
                {!isSlaveFilter && <h3>{caption}</h3>}
                {items.map((item, itemIndex) => {
                  const isMandatory = isMandatoryFilter(dataset, item.name, id);
                  return (
                    <Checkbox
                      key={itemIndex}
                      value={item.value}
                      onChange={(value) =>
                        setPopoverFilterValue(value, id, item.name)
                      }
                      label={
                        <InputLabel hint={item.hint}>
                          {item.value
                            ? item.label
                            : highlightMatch(item.label, searchString)}
                        </InputLabel>
                      }
                      color="secondary"
                      disabled={isMandatory}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          <div className="select-filters-footer">
            <Input
              name="search"
              placeholder={t('search')}
              icon={searchOutline}
              value={searchString}
              onChange={setSearchString}
              inputRef={searchInputRef}
            />
            <Button
              onClick={() => setPopoverOpen(false)}
              fill="clear"
              color="secondary"
              className="submit-btn"
            >
              {t('apply')}
            </Button>
          </div>
        </div>
      </IonPopover>
    </div>
  );
};

export default SelectFiltersPopover;
